import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import type { HeroNews } from "../constants";

export type NewsPostSimple = {
  updatedAt?: string;
  id: number;
  name: string;
  slug: string;
  postType: "news" | "amber-radar" | "amber-report";
  mainImage: {
    id: number;
    alt: string;
    url: string;
    filename?: string;
    mimeType?: string;
    filesize?: number;
    width?: number;
    height?: number;
  };
  category: {
    id: number;
    name: string;
    slug: string;
  };
  date: string;
  readTime: string;
};

export type NewsCategory = {
  id: number;
  name: string;
  slug: string;
};

interface NewsHomeData {
  readyStatus: "invalid" | "request" | "success" | "failure";
  homeFeaturedNews: HeroNews[];
  sidebarFeaturedNews: NewsPostSimple[];
  latestNews: NewsPostSimple[];
  categories: NewsCategory[];
  activeCategory: string;
  activeCategorySlug: string;
  categoryNews: NewsPostSimple[];
  categoryNewsLoading: boolean;
  amberReports: NewsPostSimple[];
  amberReportsLoading: boolean;
  amberRadar: NewsPostSimple[];
  amberRadarLoading: boolean;
  totalDocs: number;
  error: any;
}

export const initialState: NewsHomeData = {
  readyStatus: "invalid",
  homeFeaturedNews: [],
  sidebarFeaturedNews: [],
  latestNews: [],
  categories: [],
  activeCategory: "latest",
  activeCategorySlug: "",
  categoryNews: [],
  categoryNewsLoading: false,
  amberReports: [],
  amberReportsLoading: false,
  amberRadar: [],
  amberRadarLoading: false,
  totalDocs: 0,
  error: null,
};

const newsHomeSlice = createSlice({
  name: "newsHomeData",
  initialState,
  reducers: {
    updateNewsHomeData: (state: NewsHomeData, { payload }: PayloadAction<any>) => {
      state.homeFeaturedNews = payload?.homeFeaturedNews || [];
      state.sidebarFeaturedNews = payload?.sidebarFeaturedNews || [];
      state.latestNews = payload?.latestNews || [];
      state.categories = payload?.categories || [];
      state.totalDocs = payload?.totalDocs || 0;
      state.amberReports = payload?.amberReports || [];
      state.amberRadar = payload?.amberRadar || [];
    },
    updateNewsHomeReadyStatus: (
      state: NewsHomeData,
      { payload }: PayloadAction<"invalid" | "request" | "success" | "failure">,
    ) => {
      state.readyStatus = payload;
    },
    updateNewsHomeError: (state: NewsHomeData, { payload }: PayloadAction<any>) => {
      state.error = payload;
    },
    setActiveCategory: (
      state: NewsHomeData,
      { payload }: PayloadAction<{ name: string; slug: string }>,
    ) => {
      state.activeCategory = payload.name;
      state.activeCategorySlug = payload.slug;
    },
    updateCategoryNews: (state: NewsHomeData, { payload }: PayloadAction<NewsPostSimple[]>) => {
      state.categoryNews = payload;
      state.categoryNewsLoading = false;
    },
    setCategoryNewsLoading: (state: NewsHomeData, { payload }: PayloadAction<boolean>) => {
      state.categoryNewsLoading = payload;
    },
    updateAmberReports: (state: NewsHomeData, { payload }: PayloadAction<NewsPostSimple[]>) => {
      state.amberReports = payload;
      state.amberReportsLoading = false;
    },
    setAmberReportsLoading: (state: NewsHomeData, { payload }: PayloadAction<boolean>) => {
      state.amberReportsLoading = payload;
    },
    resetNewsHomeData: (state: NewsHomeData) => {
      state.readyStatus = "invalid";
      state.homeFeaturedNews = [];
      state.sidebarFeaturedNews = [];
      state.latestNews = [];
      state.categories = [];
      state.activeCategory = "latest";
      state.activeCategorySlug = "";
      state.categoryNews = [];
      state.categoryNewsLoading = false;
      state.amberReports = [];
      state.amberReportsLoading = false;
      state.amberRadar = [];
      state.amberRadarLoading = false;
      state.totalDocs = 0;
      state.error = null;
    },
  },
});

export const {
  updateNewsHomeData,
  updateNewsHomeReadyStatus,
  updateNewsHomeError,
  setActiveCategory,
  updateCategoryNews,
  setCategoryNewsLoading,
  updateAmberReports,
  setAmberReportsLoading,
  resetNewsHomeData,
} = newsHomeSlice.actions;

export default newsHomeSlice.reducer;
